// Pure lookup helpers + raw→display adapter for the Consistency intelligence drawer.
import {
  FINDING_SEVERITY_TONE,
  FINDING_SEVERITY_RANK,
  NEUTRAL_TONE,
  REFERENCE_STATUS_TONE,
  AMBIGUOUS_TONE,
  DOC_TYPE_COLORS,
  DOC_TYPE_FALLBACK_BADGE_CLS,
  type FindingSeverity,
  type FindingSeverityTone,
} from "../../constants";
import type {
  CDefinitionMismatch,
  CFinding,
  CReferenceGap,
  CTerminologyIssue,
  ConsistencyDisplayData,
  ConsistencyRawFinding,
  ConsistencyResult,
} from "./types";

export function severityTone(
  s: CFinding["severity"] | string | null | undefined,
): FindingSeverityTone {
  const key = String(s ?? "INFO").toUpperCase() as FindingSeverity;
  return FINDING_SEVERITY_TONE[key] ?? FINDING_SEVERITY_TONE.INFO;
}

export function referenceStatusTone(
  s: CReferenceGap["status"] | string | null | undefined,
): FindingSeverityTone {
  const key = String(s ?? "AMBIGUOUS").toUpperCase() as CReferenceGap["status"];
  return REFERENCE_STATUS_TONE[key] ?? AMBIGUOUS_TONE;
}

export function severityRank(s: CFinding["severity"] | string): number {
  const key = String(s ?? "INFO").toUpperCase() as FindingSeverity;
  return FINDING_SEVERITY_RANK[key] ?? FINDING_SEVERITY_RANK.INFO;
}

export function docTypeBadgeClass(
  type: string | null | undefined,
): string | null {
  if (!type) return null;
  const key = String(type).toUpperCase();
  return DOC_TYPE_COLORS[key] ?? DOC_TYPE_FALLBACK_BADGE_CLS;
}

export function pluralSuffix(n: number, suffix = "s"): string {
  return n === 1 ? "" : suffix;
}

export function sortFindingsBySeverity(findings: CFinding[]): CFinding[] {
  return [...findings].sort(
    (a, b) => severityRank(a.severity) - severityRank(b.severity),
  );
}

export { NEUTRAL_TONE };

/* ────────────────────────────────────────────────────────────────
   Raw → display adapter
   Maps the backend consistency-classify payload into the shape
   the drawer sub-components already know how to render.
   ──────────────────────────────────────────────────────────────── */

/** Normalise a severity string to the display union. */
function normaliseSeverity(
  s: string | undefined | null,
): CFinding["severity"] {
  const key = String(s ?? "INFO").toUpperCase();
  if (key === "HIGH" || key === "MEDIUM" || key === "LOW" || key === "INFO") {
    return key;
  }
  return "INFO";
}

/** Map a raw status → reference-gap status. */
function mapRefStatus(
  raw: string | undefined | null,
): CReferenceGap["status"] {
  const key = String(raw ?? "").toUpperCase();
  if (key === "MISSING" || key === "UNRESOLVED") return "UNRESOLVED";
  if (key === "AMBIGUOUS") return "AMBIGUOUS";
  return "RESOLVED";
}

function firstLine(text: string | undefined | null): string {
  const s = (text ?? "").trim();
  if (!s) return "";
  return s.split(/\n/)[0].slice(0, 160);
}

function titleCase(s: string): string {
  if (!s) return s;
  return s
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function toTerminology(f: ConsistencyRawFinding): CTerminologyIssue {
  return {
    term: f.entity,
    variants: [],
    occurrences: 1,
    recommendation: f.description,
    severity: normaliseSeverity(f.severity),
    locations: f.source_evidence ? [firstLine(f.source_evidence)] : [],
  };
}

function toDefinitionMismatch(f: ConsistencyRawFinding): CDefinitionMismatch {
  const definition = f.source_evidence
    ? { text: f.source_evidence, location: firstLine(f.source_evidence) }
    : { text: f.description, location: "-" };
  const sev = normaliseSeverity(f.severity);
  return {
    concept: f.entity,
    definitions: [definition],
    divergence_score: f.confidence ?? 0,
    severity: (sev === "INFO" ? "LOW" : sev) as CDefinitionMismatch["severity"],
  };
}

function toReferenceGap(f: ConsistencyRawFinding): CReferenceGap {
  return {
    reference_type: titleCase(String(f.category)),
    referenced_item: f.entity,
    found_in: firstLine(f.source_evidence),
    status: mapRefStatus(f.status),
    note: f.description,
  };
}

function toFinding(f: ConsistencyRawFinding): CFinding {
  const category = titleCase(String(f.category));
  const statusLabel = titleCase(String(f.status));
  const findingType = statusLabel ? `${statusLabel} ${category}` : category;
  return {
    finding_type: findingType || "Consistency issue",
    affected_area: f.entity,
    description: f.description,
    severity: normaliseSeverity(f.severity),
    source_location: f.source_evidence ? [firstLine(f.source_evidence)] : [],
  };
}

/* Weight findings by severity to compute a 0..1 consistency score. */
const SEVERITY_WEIGHT: Record<CFinding["severity"], number> = {
  HIGH: 1,
  MEDIUM: 0.5,
  LOW: 0.2,
  INFO: 0.05,
};

function computeConsistencyScore(
  findings: CFinding[],
  chunksTotal: number,
): number {
  if (findings.length === 0) return 1;
  const denom = chunksTotal > 0 ? chunksTotal : findings.length;
  const weighted = findings.reduce(
    (acc, f) => acc + (SEVERITY_WEIGHT[f.severity] ?? 0),
    0,
  );
  const score = 1 - weighted / denom;
  return Math.max(0, Math.min(1, score));
}

export function adaptConsistencyResult(
  raw: ConsistencyResult,
  documentTitle?: string,
  documentType?: string,
): ConsistencyDisplayData {
  const rawFindings = raw.findings ?? [];
  const totals = raw.metrics?.totals ?? {};

  const terminology = rawFindings
    .filter((f) => String(f.category).toUpperCase() === "TERMINOLOGY")
    .map(toTerminology);
  const definitionMismatches = rawFindings
    .filter((f) => String(f.category).toUpperCase() === "DEFINITION")
    .map(toDefinitionMismatch);
  const referenceGaps = rawFindings
    .filter((f) => String(f.category).toUpperCase() === "SCOPE")
    .map(toReferenceGap);
  const findings = rawFindings.map(toFinding);

  const chunksTotal = raw.metrics?.chunks_total ?? 0;
  const statementsAnalysed =
    chunksTotal || raw.metrics?.proposed || rawFindings.length;
  const consistencyScore = computeConsistencyScore(findings, chunksTotal);

  return {
    document: {
      document_id: raw.documentId ?? "",
      title: documentTitle ?? "",
      document_type: documentType,
    },
    summary: {
      statements_analysed: statementsAnalysed,
      contradictions_detected: 0,
      terminology_variants:
        (totals.TERMINOLOGY as number | undefined) ?? terminology.length,
      definition_mismatches:
        (totals.DEFINITION as number | undefined) ?? definitionMismatches.length,
      unresolved_references:
        (totals.SCOPE as number | undefined) ?? referenceGaps.length,
      consistency_score: consistencyScore,
      findings: raw.metrics?.findings_returned ?? findings.length,
    },
    internal_contradictions: [],
    terminology,
    definition_mismatches: definitionMismatches,
    reference_gaps: referenceGaps,
    findings,
    backendNote: raw.summary?.trim() || undefined,
  };
}
